import { ChatBotConversationProps } from "@/components/lazo-ai-chat-components/ChatBotConversationProps";
import ChatTextFieldComponent from "@/components/lazo-ai-chat-components/ChatTextFieldComponent";
import LazoAIChatWelcomeMessage from "@/components/lazo-ai-chat-components/LazoAIChatWelcomeMessage";
import { useSelectedChart } from "@/contexts/ChartsContext";
import { useLazoAIChat } from "@/contexts/FilterConversationContext";
import { useFundraising } from "@/contexts/FunderContext";
import { useRightDrawer } from "@/contexts/RightDrawerContext";
import { ConversationStateChange, useSelectedConversation } from "@/contexts/SelectedConversationContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { ChatMessageFragmentType } from "@/models/ai-chat/ChatMessageFragment";
import { ChatMessageStream } from "@/models/ai-chat/ChatMessageStream";
import { HelpMeWriteMessage } from "@/models/ai-chat/HelpMeWriteMessage";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { ConversationType } from "@/types/ConversationType";
import { HelpMeWriteType } from "@/types/HelpMeWriteType";
import { extractHourAndMinutes, getElementDateGroup } from "@/utils/DateGroup";
import { useConversationTypeIdentifier } from "@/utils/LocationUtils";
import ChatUserWebSocketClient from "@/utils/websocket/ChatUserWebSocketClient";
import { Box, Divider, Stack, StackProps, Typography, styled } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem } from "react-mentions";
import InfoIcon from "../../../assets/images/warning2.svg";
import ChatAvatar from "./ChatAvatar";
import { LazoAIChatTypingComponent } from "./LazoAIChatTypingComponent";
import { ChatbotMessage } from "./chat-message/ChatbotMessage";
import { StreamedChatMessage } from "./chat-message/StreamedChatMessage";

// Note: INITIAL_RESPONSE_MESSAGE_MAP is now replaced by getInitialResponseMessage function that uses i18n

// Using the user externalId as the key on our dictionary
const StyledStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "lazoUser",
})(({ theme }) => ({
  display: "flex",
  alignSelf: "center",
  alignItems: "center",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  backgroundColor: Colors.WHITE,
  "&.MuiStack-root>:not(style)~:not(style)": {
    marginTop: 0,
    paddingBottom: "0.5rem",
  },
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
  [theme.breakpoints.up("lg")]: {
    width: "100%",
  },
}));

const StyledAnswerVerificationFooter = styled(Typography)(() => ({
  paddingTop: "0.5rem",
  width: "95%",
  fontSize: ".625rem",
  fontWeight: 400,
  lineHeight: ".75rem",
  textAlign: "center",
  color: Colors.DARKER_GRAY,
  wordWrap: "break-word",
  whiteSpace: "normal",
  overflowWrap: "break-word",
}));

type LazoAIChatBotProps = ChatBotConversationProps &
  StackProps & {
    conversationPromise?: (tenantExternalId: string) => Promise<ApiResponseV2<LazoChatConversation>>;
  };

export const LazoAIChatContainer = (props: LazoAIChatBotProps) => {
  const { t } = useTranslation("chat");
  const { selectedChart, selectedStatementItem, setSelectedChart, setSelectedStatementItem } = useSelectedChart();
  const { selectedFunder, setSelectedFunder } = useFundraising();
  const { entityExternalId: contextEntityExternalId } = useRightDrawer();
  const messageRefs = useRef(new Map());
  const [controller, setController] = useState(new AbortController());
  const [signal, setSignal] = useState(controller.signal);
  const [currentMessage, setCurrentMessage] = useState<ChatMessageStream>();
  const { scrollToMessageExternalId } = useLazoAIChat();
  const conversationType = useConversationTypeIdentifier();
  const { currentTenantMembership } = useTenantMemberships();

  const [messages, setMessages] = useState<LazoChatMessage[]>([]);

  const [messageReceiverIsBot, setmessageReceiverIsBot] = useState<boolean>(true);

  const [loadingConversation, setLoadingConversation] = useState<boolean>(false);

  const [isWaitingResponse, setIsWaitingResponse] = useState<boolean>(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const [typingEvent, setTypingEvent] = useState<LazoChatTypingDTO | undefined>(undefined);

  // Function to get initial response message based on conversation type
  const getInitialResponseMessage = (convType: ConversationType): string | undefined => {
    switch (convType) {
      case ConversationType.ACCOUNTING:
        return t("aiDrawer.container.initialResponseMessages.accounting");
      case ConversationType.FUNDRAISING:
        return t("aiDrawer.container.initialResponseMessages.fundraising");
      case ConversationType.DATA_ROOM:
        return t("aiDrawer.container.initialResponseMessages.dataRoom");
      case ConversationType.VIRTUAL_DATA_ROOM:
        return t("aiDrawer.container.initialResponseMessages.virtualDataRoom");
      case ConversationType.SHARED_FINANCIALS:
        return t("aiDrawer.container.initialResponseMessages.sharedFinancials");
      case ConversationType.SINGLE_DOCUMENT:
        return t("aiDrawer.container.initialResponseMessages.singleDocument");
      case ConversationType.SINGLE_DOCUMENT_DATA_ROOM:
        return t("aiDrawer.container.initialResponseMessages.singleDocumentDataRoom");
      default:
        return undefined;
    }
  };

  // START CONVERSATION HANDLERS
  const selectedConversation = useRef<LazoChatConversation | null>(null);
  const { setSelectedConversation, restoreCurrentPageConversation } = useSelectedConversation();
  ChatUserWebSocketClient.subscribeRaw(AuthService.getUser()?.externalId!, (s) => handleWsMessage(s.body));
  useConversationHandlers({
    selectedConversation,
    setLoadingConversation,
    setMessages,
    restoreCurrentPageConversation,
  });
  // END CONVERSATION HANDLERS

  // START WEBSOCKET HANDLERS

  // Temp solution to avoid duplicated messages
  const lastReceivedBotMessage = useRef<string>();
  const lastReceivedBotChunk = useRef<string>();

  const handleWsMessage = (jsonMessage: string) => {
    const message = JSON.parse(jsonMessage);

    switch (message.type) {
      case "NEW_MESSAGE":
        receiveMessage(message.content);
        break;
      case "BOT_MESSAGE_START":
        receiveStartBotMessage();
        break;
      case "BOT_ERROR":
        receiveBotError();
        break;
      case "BOT_MESSAGE_CHUNK":
        if (message.content == lastReceivedBotChunk?.current) return;

        lastReceivedBotChunk.current = message.content;
        receiveBotMessageChunk(message.content);
        break;
      case "BOT_MESSAGE_METADATA":
        receiveBotMessageMetadata(message.content);
        break;
      case "BOT_MESSAGE_END":
        if (message.content == lastReceivedBotMessage?.current) return;

        lastReceivedBotMessage.current = message.content;
        finalizeBotMessage(message.content, message.externalId);
        break;
      case "CONVERSATION_INVITE":
        break;
      case "USER_TYPING":
        setTypingEvent(message.content);
        break;
    }
  };

  const receiveMessage = (message: LazoChatMessage) => {
    const isCurrentConversation = message.conversationExternalId === selectedConversation.current?.externalId;

    if (isCurrentConversation) {
      addMessageToCurrentConversation(message);
      LazoChatConversationService.readConversation(message.conversationExternalId!);
    }
  };

  const receiveBotError = () => {
    console.log("ERROR RECEVIVED!!!!");
    currentMessage?.complete(t("aiDrawer.container.errors.botError"));
  };

  const receiveStartBotMessage = () => {
    if (!currentMessage) {
      const initialContent = getInitialResponseMessage(selectedConversation.current?.conversationType || conversationType);
      setCurrentMessage(new ChatMessageStream(initialContent));
      setIsWaitingResponse(true);
    }
  };

  const receiveBotMessageChunk = (botMessageChunk: string) => {
    currentMessage?.receiveContent(botMessageChunk);
  };

  const receiveBotMessageMetadata = (metadata: string) => {
    currentMessage?.receiveMetadata(metadata);
  };

  const finalizeBotMessage = (botMessage: string, messageExternalId: string) => {
    currentMessage?.complete(botMessage, messageExternalId);
  };

  // END WEBSOCKET HANDLERS

  useEffect(() => {
    if (selectedChart) {
      handleCreateNewChartConversation(
        t("aiDrawer.container.conversationCreation.fromChart", {
          chartName: StringUtils.toTitleCase(selectedChart!.toString()),
        })
      );
    }
    if (selectedStatementItem) {
      handleCreateNewFinancialStatementItemConversation(
        t("aiDrawer.container.conversationCreation.fromStatementItem", {
          itemName: StringUtils.toTitleCase(selectedStatementItem.name),
        })
      );
    }
    if (selectedFunder) {
      handleCreateNewFunderConversation(
        t("aiDrawer.container.conversationCreation.fromFunder", {
          funderName: StringUtils.toTitleCase(selectedFunder.entity.name),
        })
      );
    }
    return () => {
      setSelectedStatementItem(null);
      setSelectedFunder(null);
      setSelectedChart(null);
    };
  }, [selectedChart, selectedStatementItem, selectedFunder]);

  messageRefs.current = new Map();
  messages.forEach((message) => {
    messageRefs.current.set(message.externalId, React.createRef());
  });

  if (bottomRef.current) {
    bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }

  useEffect(() => {
    setSignal(controller.signal);
  }, [controller]);

  useEffect(() => {
    const ref = messageRefs.current.get(scrollToMessageExternalId);

    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollToMessageExternalId]);

  function addMessageToCurrentConversation(message: LazoChatMessage) {
    setMessages((prev) => prev.concat(message));
  }

  function handleCreateNewChartConversation(message: string) {
    if (selectedChart !== null && selectedChart !== undefined && currentTenantMembership.tenant) {
      setLoadingConversation(true);

      LazoChatConversationService.createNewConversationChart(message, currentTenantMembership.tenant.externalId, selectedChart, signal)
        .then((response) => {
          response.onSuccess((createdConversation) => {
            setSelectedConversation({
              ...createdConversation,
              messages: [...createdConversation.messages],
            });
          });
        })
        .finally(() => setLoadingConversation(false));
    }
  }

  function handleCreateNewFunderConversation(message: string) {
    setLoadingConversation(true);
    LazoChatConversationService.createNewConversationFunder(
      {
        funder: selectedFunder,
        chatTitle: message,
        tenant: { externalId: currentTenantMembership.tenant.externalId },
      },
      signal
    )
      .then((response) => {
        response.onSuccess((createdConversation) => {
          setSelectedConversation({
            ...createdConversation,
            messages: [...createdConversation.messages],
          });
        });
      })
      .finally(() => setLoadingConversation(false));
  }

  function handleCreateNewFinancialStatementItemConversation(message: string) {
    if (selectedStatementItem !== null && selectedStatementItem !== undefined) {
      setLoadingConversation(true);
      LazoChatConversationService.createNewConversationStatementTableItem(
        {
          ...selectedStatementItem,
          chatTitle: message,
          tenant: { externalId: currentTenantMembership.tenant.externalId },
        },
        signal
      )
        .then((response) => {
          response.onSuccess((createdConversation) => {
            setSelectedConversation({
              ...createdConversation,
              messages: [...createdConversation.messages],
            });
          });
        })
        .finally(() => setLoadingConversation(false));
    }
  }

  async function handleSendMessage(message: string, messageReceiverIsBot: boolean, mentions?: MentionItem[]) {
    setIsWaitingResponse(true);

    if (!selectedConversation.current) {
      setLoadingConversation(true);

      const response = props.conversationPromise
        ? await props.conversationPromise(currentTenantMembership.tenant.externalId)
        : await LazoChatConversationService.createNewConversation(
            message,
            currentTenantMembership.tenant,
            conversationType,
            signal,
            contextEntityExternalId
          );

      response.onError((error) => {
        NotificationService.error(error);
      });

      response.onSuccess((createdConversation) => {
        selectedConversation.current = createdConversation;
        setSelectedConversation(createdConversation, ConversationStateChange.CONVERSATION_CREATED);
      });
    }

    await sendRealMessage(message, selectedConversation.current.externalId, messageReceiverIsBot);

    if (mentions && mentions.length > 0) {
      LazoChatConversationService.addMentionsToConversationMembers(mentions, selectedConversation.current.externalId)
        .then((r) => console.log(r))
        .catch((e: { message: string }) => {
          console.log(e.message);
        });
    }
  }

  // TODO: Change this name, simplifiy sendMessage method and merge both
  const sendRealMessage = async (message: string, conversationId: string, messageReceiverIsBot: boolean) => {
    const response = await LazoChatMessageService.createNewMessage(message, conversationId, messageReceiverIsBot, signal);

    response.onError(() => {
      NotificationService.error(t("aiDrawer.container.errors.sendingMessage"));
    });

    response.onSuccess((message) => setMessages((prev) => prev.concat(message)));

    if (!messageReceiverIsBot) {
      setIsWaitingResponse(false);
      setCurrentMessage(undefined);
    }

    setLoadingConversation(false);
  };

  const explainPreviousMessage = (message: string, conversationId: string) => {
    setIsWaitingResponse(true);
    LazoChatMessageService.sendHiddenMessage(message, conversationId, signal).then((response) => {
      response.onError(() => {
        NotificationService.error(t("aiDrawer.container.errors.sendingMessage"));
      });
    });
  };

  const helpMeWrite = (conversationId: string, type: HelpMeWriteType) => {
    setIsWaitingResponse(true);
    const helpMeWriteMessage: HelpMeWriteMessage = {
      chatMessage: {
        conversationExternalId: conversationId,
        message: "",
      },
      funderDTO: selectedFunder,
      helpMeWriteType: type,
    };
    LazoChatMessageService.helpMeWriteMessage(helpMeWriteMessage, signal).then((response) => {
      response.onError(() => {
        NotificationService.error(t("aiDrawer.container.errors.sendingMessage"));
      });
    });
  };

  const handleStreamedMessageFinish = () => {
    if (!currentMessage) return;

    addMessageToCurrentConversation({
      user: undefined,
      externalId: currentMessage.externalId,
      messageReceiverIsBot: messageReceiverIsBot,
      createdAt: new Date().toISOString(),
      message: currentMessage.content,
      isBotMessage: true,
      metadata: currentMessage.metadata ? JSON.parse(currentMessage.metadata) : undefined,
    });

    setIsWaitingResponse(false);
    setCurrentMessage(undefined);
  };

  const handleStopBotResponse = () => {
    setIsWaitingResponse(false);
    controller.abort();
    setController(new AbortController());
  };

  const handleToggleMessageReceiver = () => {
    setmessageReceiverIsBot((state) => !state);
  };

  let lastUserAvatar: User | undefined = undefined;
  let lastCreatedAt: string[] | undefined = undefined;

  // NOTE: This code is ugly af, maybe do another thing to check for avatar rendering
  const renderAvatar = (user?: User, isBotMessage?: boolean, createdAt?: string): JSX.Element | null => {
    let renderAvatar: boolean = false;
    let renderDate: boolean = false;
    if (isBotMessage) {
      if (isBotMessage) {
        lastUserAvatar = undefined;
      }
      renderAvatar = false;
    }

    if (createdAt === undefined) {
      createdAt = new Date().toISOString();
    }

    const [_, h, m] = extractHourAndMinutes(createdAt) || extractHourAndMinutes(new Date().toISOString());
    const hoursAndMinutes = h.toString() + ":" + m.toString();
    const dateGroup = getElementDateGroup<LazoChatMessage>({
      createdAt: createdAt,
    } as LazoChatMessage);
    const messageDateGroup = dateGroup + ", " + hoursAndMinutes;
    const currentCreatedAt: string[] | undefined = createdAt.split(":");

    if (user?.firstName && user?.lastName && user?.externalId !== lastUserAvatar?.externalId) {
      renderAvatar = true;
      lastUserAvatar = user;
    }

    if (lastCreatedAt?.[0] !== currentCreatedAt?.[0] && lastCreatedAt?.[1] !== currentCreatedAt?.[1]) {
      lastCreatedAt = createdAt.split(":");
      renderDate = true;
    }

    const isCurrentUser = user?.externalId === props.lazoUser.user.externalId;

    return (
      <>
        {renderAvatar && (
          <ChatAvatar
            userAvatarStyle={{
              justifyContent: user?.externalId === props.lazoUser.user.externalId ? "end" : "start",
              paddingTop: "0.2rem",
            }}
            isBotMessage={isBotMessage}
            messageDateGroup={renderDate ? messageDateGroup : undefined}
            user={user}
            disableName={isCurrentUser || !user?.firstName || !user?.lastName}
            disableImage={isCurrentUser}
          />
        )}
      </>
    );
  };

  const handleSendExplainMessage = () => {
    console.log("selectedStatementItem", selectedStatementItem);
    const prompt = selectedStatementItem
      ? t("aiDrawer.container.explanations.comprehensiveExplanation")
      : t("aiDrawer.container.explanations.longerExplanation");
    explainPreviousMessage(prompt, selectedConversation.current.externalId);
  };

  const handleHelpMeWriteFollowUp = () => {
    helpMeWrite(selectedConversation.current.externalId, HelpMeWriteType.FOLLOW_UP_EMAIL);
  };

  const handleHelpMeWriteIntro = () => {
    helpMeWrite(selectedConversation.current.externalId, HelpMeWriteType.APPROACH_EMAIL);
  };

  return (
    <>
      <StyledStack {...props} spacing={2}>
        {loadingConversation ? (
          <Spinner size={"2rem"} sx={{ color: Colors.LAZO_PURPLE }} />
        ) : messages.length > 0 ? (
          <Box
            key="container"
            sx={{
              height: "90%",
              width: "95%",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {messages
              // HOTFIX TO PREVENT DUPLICATED REMOVED AND INVITED MESSAGES
              .filter(
                (message, index) =>
                  ![ChatMessageFragmentType.INVITED_USERS_ARRAY, ChatMessageFragmentType.REMOVED_USERS_ARRAY].includes(
                    message?.messageFragments?.[0]?.type
                  ) ||
                  (index > 0 && message.messageFragments[0].content[0] !== messages?.[index - 1]?.messageFragments?.[0]?.content?.[0])
              )
              .map((message, index) => {
                if (!message.createdAt) {
                  try {
                    message.createdAt = new Date().toISOString();
                  } catch (e) {}
                }
                return (
                  <>
                    {renderAvatar(message.user, message.isBotMessage, message.createdAt)}
                    <ChatbotMessage
                      lazoChatConversation={selectedConversation.current}
                      ref={messageRefs.current.get(message.externalId)}
                      key={`${message.externalId}-${index}`}
                      lazoUser={props.lazoUser}
                      messageEntity={message}
                      messageSender={message.user}
                      isLastMessage={index === messages.length - 1}
                      explainClick={handleSendExplainMessage}
                      approachEmailClick={handleHelpMeWriteIntro}
                      followUpEmailClick={selectedFunder?.nextContacts?.length > 0 ? handleHelpMeWriteFollowUp : undefined}
                    />
                  </>
                );
              })}

            {currentMessage && (
              <StreamedChatMessage
                key={"streamed-message"}
                lazoUser={props.lazoUser}
                streamedMessage={currentMessage}
                onStreamStart={() => {}}
                messageSender={undefined}
                handleStreamedMessageFinish={handleStreamedMessageFinish}
              />
            )}
            <div ref={bottomRef} />
          </Box>
        ) : (
          <>
            <LazoAIChatWelcomeMessage />
            {<Divider orientation="horizontal" sx={{ width: "100%" }} />}
          </>
        )}
        {/* Start of User Typing block */}
        <LazoAIChatTypingComponent message={typingEvent} />
        {/* End of User Typing block */}
        {
          /*messageReceiverIsBot &&
          conversationType === ConversationType.ACCOUNTING ? (
          <LazoAIPremadeQuestions onSendMessage={handleSendMessage} />
        ) :*/
          // Padding
          <Box sx={{ height: "1rem" }} />
        }
        <Box sx={{ width: "95%" }}>
          {messageReceiverIsBot && (
            <Box
              sx={{
                background: `linear-gradient(to bottom right, ${Colors.LAZO_PURPLE} 80%, ${Colors.LAZO_PURPLE2} 100%)`,
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",

                padding: "10px",
                color: "white",
                fontSize: ".8125rem",
                fontWeight: "500",
                height: "4rem",

                width: "100%",

                display: "flex",
                justifyContent: "start",
                position: "relative",
                zIndex: 1,
              }}
            >
              <img
                src={InfoIcon.src}
                style={{
                  color: "white",
                  marginRight: "3px",
                  display: "flex",
                  height: "50%",
                }}
              ></img>{" "}
              {t("aiDrawer.container.botInteraction.interactingWithAI")}
            </Box>
          )}
          <ChatTextFieldComponent
            hasBeenInMultiuserChat={props.lazoUser.hasBeenInMultiuserChat}
            messageReceiverIsBot={messageReceiverIsBot}
            showAskAiToggleButton={(selectedConversation.current?.members && selectedConversation.current?.members.length > 1) || false}
            isCreatingConversation={loadingConversation}
            waiting={isWaitingResponse}
            onSendMessage={handleSendMessage}
            onClickStop={handleStopBotResponse}
            handleToggleMessageReceiver={handleToggleMessageReceiver}
          />
        </Box>
        {messageReceiverIsBot && (
          <StyledAnswerVerificationFooter>{t("aiDrawer.container.verification.footer")}</StyledAnswerVerificationFooter>
        )}
      </StyledStack>
    </>
  );
};

type ConversationHandlersProps = {
  setLoadingConversation: React.Dispatch<React.SetStateAction<boolean>>;
  setMessages: React.Dispatch<React.SetStateAction<LazoChatMessage[]>>;
  selectedConversation: React.MutableRefObject<LazoChatConversation | null>;
  restoreCurrentPageConversation: (pathname: string) => void;
};

function useConversationHandlers({
  setLoadingConversation,
  setMessages,
  selectedConversation,
  restoreCurrentPageConversation,
}: ConversationHandlersProps) {
  useEffect(() => {
    setLoadingConversation(true);

    function onConversationSelected(ev: { detail: LazoChatConversation }) {
      selectedConversation.current = ev.detail || null;
      setMessages(ev.detail?.messages || []);
      setLoadingConversation(false);
    }

    function onConversationReset() {
      selectedConversation.current = null;
      setMessages([]);
      setLoadingConversation(false);
    }

    // @ts-ignore-error
    document.addEventListener(ConversationStateChange.CONVERSATION_SELECTED, onConversationSelected);
    document.addEventListener(ConversationStateChange.CONVERSATION_RESET, onConversationReset);

    if (location.search) {
      restoreCurrentPageConversation(location.pathname + location.search);
    } else {
      restoreCurrentPageConversation(location.pathname);
    }

    return () => {
      // @ts-ignore-error
      document.removeEventListener(ConversationStateChange.CONVERSATION_SELECTED, onConversationSelected);
      document.removeEventListener(ConversationStateChange.CONVERSATION_RESET, onConversationReset);
    };
  }, [location.pathname]);
}
